import { useEffect, useMemo, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { Bell, Check, CheckCircle2, RefreshCw, Search, X, AlertTriangle, Info, AlertCircle } from "lucide-react"
import {
  smartoltService,
  type SmartOLTAlarmList,
  type SmartOLTAlarmRow,
} from "../../../services/smartolt.service.ts"
import { toast } from "../../../utils/notify.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"
import { dateTime } from "../utils.ts"

const PAGE_SIZES = [25, 50, 100, 200]

const selectCls = "h-9 min-w-[120px] rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-800 shadow-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"

function severityConfig(severity: string) {
  const value = severity.toLowerCase()
  if (["critical", "emergency"].includes(value))
    return { cls: "bg-rose-50 text-rose-700 border border-rose-200 font-semibold", dot: "bg-rose-500" }
  if (["alert", "error"].includes(value))
    return { cls: "bg-orange-50 text-orange-700 border border-orange-200 font-semibold", dot: "bg-orange-500" }
  if (value === "warning")
    return { cls: "bg-amber-50 text-amber-700 border border-amber-200 font-semibold", dot: "bg-amber-500" }
  if (["notice", "informational"].includes(value))
    return { cls: "bg-sky-50 text-sky-700 border border-sky-200 font-semibold", dot: "bg-sky-500" }
  return { cls: "bg-slate-100 text-slate-700 border border-slate-200 font-semibold", dot: "bg-slate-400" }
}

function stateConfig(state: string) {
  if (state === "cleared") return { cls: "bg-emerald-50 text-emerald-700 border border-emerald-200" }
  if (state === "acked") return { cls: "bg-sky-50 text-sky-700 border border-sky-200" }
  return { cls: "bg-rose-50 text-rose-700 border border-rose-200" }
}

function stateLabel(row: SmartOLTAlarmRow) {
  if (row.state === "cleared") return "Cleared"
  if (row.state === "acked") return "Acked"
  return "Open"
}

function rowSeverityBg(severity: string) {
  const v = severity.toLowerCase()
  if (["critical", "emergency"].includes(v)) return "bg-rose-50/40 hover:bg-rose-50/70"
  if (["alert", "error"].includes(v)) return "hover:bg-orange-50/50"
  if (v === "warning") return "hover:bg-amber-50/50"
  return "hover:bg-slate-50/80"
}

export function SmartOLTAlarmsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [data, setData] = useState<SmartOLTAlarmList>({
    alarms: [],
    counts: { critical: 0, warning: 0, other: 0, open: 0, total: 0 },
    filtered_count: 0,
  })
  const [search, setSearch] = useState(() => searchParams.get("q") || "")
  const [severity, setSeverity] = useState(() => searchParams.get("severity") || "all")
  const [state, setState] = useState(() => searchParams.get("state") || "open")
  const [range, setRange] = useState(() => searchParams.get("range") || "24h")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [detail, setDetail] = useState<SmartOLTAlarmRow | null>(null)

  const load = async (silent = false) => {
    if (!silent) setLoading(true)
    setRefreshing(true)
    try {
      const next = await smartoltService.getAlarms({
        severity,
        state,
        range,
        q: search.trim() || undefined,
      })
      setData({
        alarms: Array.isArray(next?.alarms) ? next.alarms : [],
        counts: next?.counts || { critical: 0, warning: 0, other: 0, open: 0, total: 0 },
        filtered_count: next?.filtered_count || 0,
      })
    } catch {
      toast.error("Failed to load alarms")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    load()
  }, [severity, state, range])

  useEffect(() => {
    const next = new URLSearchParams()
    if (search.trim()) next.set("q", search.trim())
    if (severity !== "all") next.set("severity", severity)
    if (state !== "open") next.set("state", state)
    if (range !== "24h") next.set("range", range)
    if (searchParams.toString() !== next.toString()) setSearchParams(next, { replace: true })
  }, [search, severity, state, range, searchParams, setSearchParams])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return data.alarms
    return data.alarms.filter((row) =>
      [row.device, row.message, row.onu_name, row.olt_name, row.oid].filter(Boolean).join(" ").toLowerCase().includes(q),
    )
  }, [data.alarms, search])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const pageSafe = Math.min(page, totalPages)
  const paged = filtered.slice((pageSafe - 1) * pageSize, pageSafe * pageSize)

  const toggleAll = () => {
    const ids = paged.filter((row) => row.state !== "cleared").map((row) => row.id)
    const allOn = ids.every((id) => selected.has(id))
    setSelected((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => (allOn ? next.delete(id) : next.add(id)))
      return next
    })
  }

  const runAction = async (action: "ack" | "clear" | "notify", ids = [...selected]) => {
    if (ids.length === 0) {
      toast.error(action === "notify" ? "Select one or more critical/emergency alarms to notify." : "Select at least one alarm")
      return
    }
    if (action === "notify") {
      const ok = window.confirm(
        `Send email / WhatsApp notifications for the ${ids.length} selected alarm(s)?\nOnly critical & emergency alarms are actually sent.`,
      )
      if (!ok) return
    }
    setBusy(true)
    try {
      const result = await smartoltService.updateAlarms(action, ids)
      if (action === "notify") {
        toast.success(`Notified ${result?.alarms ?? 0} alarm(s)`)
      } else {
        toast.success(action === "ack" ? "Alarms acknowledged" : "Alarms cleared")
      }
      setSelected(new Set())
      setDetail(null)
      await load(true)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Alarm action failed")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* Page header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Alarm Console</h1>
          <p className="mt-0.5 text-xs text-slate-500">Real-time network event monitoring</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Severity count pills */}
          {data.counts.critical > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 border border-rose-200 shadow-xs">
              <span className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" />
              {data.counts.critical} critical
            </span>
          )}
          {data.counts.warning > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 border border-amber-200 shadow-xs">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              {data.counts.warning} warning
            </span>
          )}
          {data.counts.other > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-700 border border-sky-200 shadow-xs">
              <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />
              {data.counts.other} other
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 border border-slate-200 shadow-xs">
            {data.counts.open} open
          </span>
        </div>
      </div>

      {/* Main table card */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xs">
        {/* Toolbar */}
        <div className="sticky top-14 z-10 flex flex-wrap items-center gap-2.5 border-b border-slate-200 bg-slate-50/80 p-3.5 sm:p-4 backdrop-blur-sm">
          <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              placeholder="Search alarms…"
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-xs transition-colors"
            />
          </div>
          <select value={severity} onChange={(e) => { setSeverity(e.target.value); setPage(1) }} className={selectCls}>
            <option value="all">All severities</option>
            <option value="critical">Critical</option>
            <option value="emergency">Emergency</option>
            <option value="alert">Alert</option>
            <option value="error">Error</option>
            <option value="warning">Warning</option>
            <option value="notice">Notice</option>
            <option value="informational">Informational</option>
            <option value="debug">Debug</option>
          </select>
          <select value={state} onChange={(e) => { setState(e.target.value); setPage(1) }} className={selectCls}>
            <option value="open">Open (unacked)</option>
            <option value="acked">Acknowledged</option>
            <option value="cleared">Cleared</option>
            <option value="all">All states</option>
          </select>
          <select value={range} onChange={(e) => { setRange(e.target.value); setPage(1) }} className={selectCls}>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="all">All time</option>
          </select>

          <div className="flex items-center gap-1.5 ml-auto">
            <button type="button" disabled={busy || selected.size === 0} onClick={() => runAction("ack")}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-xs transition-all hover:bg-slate-50 disabled:opacity-40">
              <Check className="h-3.5 w-3.5" /> Ack
            </button>
            <button type="button" disabled={busy || selected.size === 0} onClick={() => runAction("clear")}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-xs transition-all hover:bg-slate-50 disabled:opacity-40">
              <CheckCircle2 className="h-3.5 w-3.5" /> Clear
            </button>
            <button type="button" disabled={busy} onClick={() => runAction("notify")}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 text-sm font-medium text-amber-700 shadow-xs transition-all hover:bg-amber-100 disabled:opacity-40">
              <Bell className="h-3.5 w-3.5" /> Notify
            </button>
            <button type="button" disabled={refreshing} onClick={() => load()}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white shadow-xs transition-all hover:bg-blue-700 disabled:opacity-50">
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50/80">
              <tr>
                <th className="px-3 py-2.5 text-left">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    onChange={toggleAll}
                    className="h-4 w-4 rounded border-slate-300 bg-white accent-blue-600"
                  />
                </th>
                {["Time", "Severity", "Device", "Message", "State", "Actions"].map((h, i) => (
                  <th key={h} className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600 ${i === 5 ? "text-right" : "text-left"}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-3 py-10 text-center text-slate-500">
                    <div className="flex flex-col items-center gap-2">
                      <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
                      <span className="text-xs">Loading alarms…</span>
                    </div>
                  </td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-12 text-center">
                    <AlertCircle className="mx-auto h-8 w-8 text-slate-400 mb-2" />
                    <p className="text-slate-500 text-sm">No alarms in this filter.</p>
                  </td>
                </tr>
              ) : (
                paged.map((row) => {
                  const svCfg = severityConfig(row.severity)
                  return (
                    <tr
                      key={row.id}
                      className={`cursor-pointer transition-colors ${rowSeverityBg(row.severity)}`}
                      onClick={(event) => {
                        if ((event.target as HTMLElement).closest("input,button,a")) return
                        setDetail(row)
                      }}
                    >
                      <td className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          aria-label={`Select ${row.message}`}
                          disabled={row.state === "cleared"}
                          checked={selected.has(row.id)}
                          onChange={() => {
                            setSelected((prev) => {
                              const next = new Set(prev)
                              if (next.has(row.id)) next.delete(row.id)
                              else next.add(row.id)
                              return next
                            })
                          }}
                          className="h-4 w-4 rounded border-slate-300 bg-white accent-blue-600 disabled:opacity-40"
                        />
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-slate-500 text-xs font-mono">{dateTime(row.time)}</td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase ${svCfg.cls}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${svCfg.dot}`} />
                          {row.severity}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 font-semibold text-slate-900">
                        {row.olt_id ? (
                          <Link to={SMARTOLT_PATHS.oltDetail(row.olt_id)} className="text-blue-600 hover:text-blue-800 hover:underline">
                            {row.device}
                          </Link>
                        ) : (
                          row.device
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-slate-700">
                        <div className="text-sm font-medium">{row.message}</div>
                        {row.oid ? <div className="text-[11px] text-slate-500 font-mono mt-0.5">{row.oid}</div> : null}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${stateConfig(row.state).cls}`}>
                          {stateLabel(row)}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {row.state === "cleared" ? (
                          <span className="text-xs text-slate-400">—</span>
                        ) : (
                          <div className="inline-flex gap-1">
                            {row.state !== "acked" ? (
                              <button type="button" disabled={busy} onClick={() => runAction("ack", [row.id])}
                                className="h-7 rounded-md border border-slate-200 bg-white px-2 text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors">
                                Ack
                              </button>
                            ) : null}
                            <button type="button" disabled={busy} onClick={() => runAction("clear", [row.id])}
                              className="h-7 rounded-md border border-emerald-200 bg-emerald-50 px-2 text-xs font-medium text-emerald-700 hover:bg-emerald-100 shadow-xs transition-colors">
                              Clear
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/80 px-4 py-3 text-xs text-slate-600">
          <p>
            Showing {filtered.length === 0 ? 0 : (pageSafe - 1) * pageSize + 1}–{Math.min(pageSafe * pageSize, filtered.length)} of {filtered.length}
          </p>
          <div className="flex items-center gap-2">
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}
              className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700 shadow-xs">
              {PAGE_SIZES.map((size) => <option key={size} value={size}>{size} / page</option>)}
            </select>
            <button type="button" disabled={pageSafe <= 1} onClick={() => setPage((p) => p - 1)}
              className="h-8 rounded-lg border border-slate-200 bg-white px-3 text-slate-700 hover:bg-slate-50 shadow-xs disabled:opacity-40 transition-colors font-medium">
              ‹ Prev
            </button>
            <span className="px-1 font-semibold text-slate-700">{pageSafe} / {totalPages}</span>
            <button type="button" disabled={pageSafe >= totalPages} onClick={() => setPage((p) => p + 1)}
              className="h-8 rounded-lg border border-slate-200 bg-white px-3 text-slate-700 hover:bg-slate-50 shadow-xs disabled:opacity-40 transition-colors font-medium">
              Next ›
            </button>
          </div>
        </div>
      </div>

      {/* Alarm Detail Modal */}
      {detail ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs" onClick={() => setDetail(null)}>
          <div
            className="w-full max-w-xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl animate-modal"
            onClick={(event) => event.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-start justify-between border-b border-slate-200 bg-slate-50/80 px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-50 border border-rose-200">
                  <AlertTriangle className="h-4 w-4 text-rose-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-slate-900">Alarm Detail</h2>
                  <p className="text-[11px] text-slate-500">ID: {detail.id}</p>
                </div>
              </div>
              <button type="button" onClick={() => setDetail(null)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <dl className="grid grid-cols-[100px_1fr] gap-x-4 gap-y-3 text-sm">
                {[
                  ["Time", dateTime(detail.time)],
                  ["Severity", detail.severity],
                  ["State", stateLabel(detail)],
                  ["Message", detail.message],
                  ["OID", detail.oid || "—"],
                  ["Detail", detail.content || "—"],
                ].map(([label, value]) => (
                  <div key={label as string} className="contents">
                    <dt className="self-start text-slate-500 pt-0.5">{label}</dt>
                    <dd className={`text-slate-800 ${label === "OID" || label === "Detail" ? "font-mono text-xs text-slate-600" : "font-medium"}`}>
                      {value as string}
                    </dd>
                  </div>
                ))}
                <div className="contents">
                  <dt className="text-slate-500">Device</dt>
                  <dd>
                    {detail.olt_id
                      ? <Link to={SMARTOLT_PATHS.oltDetail(detail.olt_id)} className="text-blue-600 font-semibold hover:underline">{detail.device}</Link>
                      : <span className="text-slate-900 font-semibold">{detail.device}</span>}
                    {detail.onu_external_id ? (
                      <> · <Link to={SMARTOLT_PATHS.onuDetail(detail.onu_external_id)} className="text-blue-600 font-semibold hover:underline">{detail.onu_name || detail.onu_external_id}</Link></>
                    ) : null}
                  </dd>
                </div>
              </dl>
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 bg-slate-50/60 px-5 py-4">
              {detail.state === "open" && (
                <button type="button" disabled={busy} onClick={() => runAction("ack", [detail.id])}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors">
                  Acknowledge
                </button>
              )}
              {detail.state !== "cleared" && (
                <button type="button" disabled={busy} onClick={() => runAction("clear", [detail.id])}
                  className="h-9 rounded-lg border border-emerald-200 bg-emerald-50 px-4 text-sm font-medium text-emerald-700 hover:bg-emerald-100 shadow-xs transition-colors">
                  Clear
                </button>
              )}
              <button type="button" disabled={busy} onClick={() => runAction("notify", [detail.id])}
                className="h-9 rounded-lg border border-amber-200 bg-amber-50 px-4 text-sm font-medium text-amber-700 hover:bg-amber-100 shadow-xs transition-colors">
                Notify
              </button>
              <button type="button" onClick={() => setDetail(null)}
                className="h-9 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white shadow-xs hover:bg-blue-700 transition-all">
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
